import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

// A single looping exchange shown in the hero demo. Keep short — hero animation,
// not a reading exercise.
const DEMO_USER_LINE = 'examine the letter';
const DEMO_AI_LINE =
  "You turn the envelope over in your hands. Heavy paper, almost card-stock thick, sealed with a disk of black wax that's been pressed with some kind of sigil — not a crest you recognize. The ink is faintly scented with lavender.";

// Context chips that materialize between the user prompt and the AI response.
// They hint at what the engine is consulting — place, character, memory, thread —
// without naming anything technical. Tied to the demo narrative above.
const DEMO_CONTEXT_CHIPS = [
  { emoji: '📍', text: 'Your rooms in Bloomsbury' },
  { emoji: '👤', text: 'You, quietly uneasy' },
  { emoji: '🧠', text: 'The last black-wax letter, six years ago' },
  { emoji: '🔗', text: 'The bookseller downstairs is watching' },
] as const;

type DemoPhase =
  | { kind: 'typing_user'; chars: number }
  | { kind: 'pause_after_user' }
  | { kind: 'composing'; chipIdx: number }
  | { kind: 'typing_ai'; chars: number }
  | { kind: 'pause_after_ai' };

// Typewriter-style looping exchange with a "composing context" step between
// prompt and response. Self-contained; resets every ~12s.
function DemoExchange() {
  const [phase, setPhase] = useState<DemoPhase>({ kind: 'typing_user', chars: 0 });

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    if (phase.kind === 'typing_user') {
      if (phase.chars < DEMO_USER_LINE.length) {
        timeout = setTimeout(() => setPhase({ kind: 'typing_user', chars: phase.chars + 1 }), 45);
      } else {
        timeout = setTimeout(() => setPhase({ kind: 'pause_after_user' }), 500);
      }
    } else if (phase.kind === 'pause_after_user') {
      timeout = setTimeout(() => setPhase({ kind: 'composing', chipIdx: 0 }), 350);
    } else if (phase.kind === 'composing') {
      if (phase.chipIdx < DEMO_CONTEXT_CHIPS.length) {
        timeout = setTimeout(
          () => setPhase({ kind: 'composing', chipIdx: phase.chipIdx + 1 }),
          350,
        );
      } else {
        timeout = setTimeout(() => setPhase({ kind: 'typing_ai', chars: 0 }), 650);
      }
    } else if (phase.kind === 'typing_ai') {
      if (phase.chars < DEMO_AI_LINE.length) {
        timeout = setTimeout(() => setPhase({ kind: 'typing_ai', chars: phase.chars + 1 }), 18);
      } else {
        timeout = setTimeout(() => setPhase({ kind: 'pause_after_ai' }), 3200);
      }
    } else if (phase.kind === 'pause_after_ai') {
      timeout = setTimeout(() => setPhase({ kind: 'typing_user', chars: 0 }), 400);
    }
    return () => clearTimeout(timeout);
  }, [phase]);

  const userText =
    phase.kind === 'typing_user'
      ? DEMO_USER_LINE.slice(0, phase.chars)
      : phase.kind === 'pause_after_user' ||
          phase.kind === 'composing' ||
          phase.kind === 'typing_ai' ||
          phase.kind === 'pause_after_ai'
        ? DEMO_USER_LINE
        : '';

  const aiText =
    phase.kind === 'typing_ai'
      ? DEMO_AI_LINE.slice(0, phase.chars)
      : phase.kind === 'pause_after_ai'
        ? DEMO_AI_LINE
        : '';

  const chipsVisible =
    phase.kind === 'composing'
      ? phase.chipIdx
      : phase.kind === 'typing_ai' || phase.kind === 'pause_after_ai'
        ? DEMO_CONTEXT_CHIPS.length
        : 0;

  const showComposingLabel = phase.kind === 'composing';

  return (
    <div className="bg-dark-800/90 backdrop-blur rounded-xl border border-dark-500/60 shadow-2xl p-6 md:p-8 min-h-[320px] font-serif">
      <div className="text-primary-400 text-sm font-mono mb-3">&gt; {userText}
        {phase.kind === 'typing_user' && <BlinkingCursor />}
      </div>

      {chipsVisible > 0 && (
        <div className="mb-4">
          {showComposingLabel && (
            <div className="text-[10px] uppercase tracking-[0.18em] text-dark-400 mb-2 font-sans animate-fadeIn">
              drawing on the world<span className="animate-pulse">…</span>
            </div>
          )}
          <div className="flex flex-wrap gap-2 font-sans">
            {DEMO_CONTEXT_CHIPS.slice(0, chipsVisible).map((chip, i) => (
              <span
                key={i}
                className="animate-fadeIn inline-flex items-center gap-1.5 bg-primary-900/30 border border-primary-700/40 rounded-full px-2.5 py-1 text-xs text-primary-100"
              >
                <span aria-hidden="true">{chip.emoji}</span>
                <span>{chip.text}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {aiText && (
        <div className="text-dark-100 text-base md:text-lg leading-relaxed">
          {aiText}
          {phase.kind === 'typing_ai' && <BlinkingCursor />}
        </div>
      )}
    </div>
  );
}

function BlinkingCursor() {
  return <span className="inline-block w-[2px] h-[1em] align-text-bottom ml-0.5 bg-primary-400 animate-pulse" />;
}

function FeatureCard({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div className="bg-dark-700/60 border border-dark-500 rounded-lg p-6">
      <div className="text-3xl mb-3">{icon}</div>
      <h3 className="text-lg font-semibold text-dark-50 mb-2">{title}</h3>
      <p className="text-sm text-dark-300 leading-relaxed">{body}</p>
    </div>
  );
}

// Subsystems that fire every turn. Surfaced to the user in plain language —
// no field names, no agent names, no task types. Selling the shape of what
// we do, not the implementation.
const ENGINE_NODES: ReadonlyArray<{ emoji: string; title: string; body: string }> = [
  {
    emoji: '🧬',
    title: 'Character state',
    body: 'Every NPC\'s mood, goals, and secrets — updated after every turn, not re-guessed.',
  },
  {
    emoji: '📚',
    title: 'Scene memory',
    body: 'The exact earlier moments that matter right now are retrieved and threaded back in.',
  },
  {
    emoji: '🎯',
    title: 'Scene direction',
    body: 'Pacing, tension, and tone are actively guarded so the story keeps its shape.',
  },
  {
    emoji: '🛡️',
    title: 'Continuity guards',
    body: 'Characters don\'t forget themselves. Places don\'t shift. Threads don\'t break.',
  },
  {
    emoji: '✨',
    title: 'Entity capture',
    body: 'New people, places, and hooks are absorbed into the world the moment you introduce them.',
  },
  {
    emoji: '🪄',
    title: 'Adaptive prompt',
    body: 'The instructions your model receives are rewritten from scratch, turn by turn.',
  },
];

// One "spotlight" ripples through the nodes on a slow loop, suggesting that
// these all fire continuously. No scroll-triggered reveal — it runs always.
function useEngineSpotlight() {
  const [activeIdx, setActiveIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setActiveIdx((i) => (i + 1) % ENGINE_NODES.length);
    }, 900);
    return () => clearInterval(id);
  }, []);
  return activeIdx;
}

function ArchitectureSection() {
  const activeIdx = useEngineSpotlight();

  return (
    <section className="px-6 md:px-10 py-16 md:py-24 max-w-6xl mx-auto relative">
      <div className="text-center mb-12">
        <div className="inline-block text-[10px] uppercase tracking-[0.24em] text-primary-400/80 border border-primary-500/30 rounded-full px-3 py-1 mb-5">
          Under the hood
        </div>
        <h2 className="text-3xl md:text-5xl font-bold text-dark-50 mb-5 leading-tight">
          Not a chat. <span className="text-primary-400">A narrative engine.</span>
        </h2>
        <p className="text-dark-200 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
          Chat apps give the model your last few messages. Textborne composes a living world every turn — so the prose stays sharp, the characters stay themselves, and the story holds together the deeper you go.
        </p>
      </div>

      {/* Diagram */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,260px)_1fr] gap-8 lg:gap-14 items-center">
        {/* Left: the prompt source */}
        <div className="relative">
          <div className="bg-dark-800/95 border border-primary-700/40 rounded-xl p-5 shadow-[0_0_40px_-10px_rgba(129,140,248,0.35)]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-dark-400 mb-3">You type</div>
            <div className="text-primary-300 font-mono text-sm leading-relaxed">
              &gt; ask her about the letter
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 text-xs text-dark-400 font-sans">
            <span>one line in</span>
            <span className="flex-1 h-px bg-gradient-to-r from-primary-500/50 to-transparent" />
            <span className="text-primary-400">⇢</span>
          </div>
          <p className="mt-3 text-xs text-dark-400 leading-relaxed">
            Then, before a single word of response is written, <span className="text-dark-200">six subsystems</span> fire in parallel.
          </p>
        </div>

        {/* Right: subsystem grid with rolling spotlight */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ENGINE_NODES.map((node, i) => {
            const active = i === activeIdx;
            return (
              <div
                key={i}
                className={`rounded-lg p-4 border transition-all duration-500 ${
                  active
                    ? 'bg-primary-900/30 border-primary-500/70 shadow-[0_0_30px_-8px_rgba(129,140,248,0.6)]'
                    : 'bg-dark-800/60 border-dark-500'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="text-2xl leading-none pt-0.5" aria-hidden="true">{node.emoji}</div>
                  <div className="min-w-0">
                    <h3 className={`font-semibold text-sm mb-1 transition-colors ${active ? 'text-primary-200' : 'text-dark-50'}`}>
                      {node.title}
                    </h3>
                    <p className="text-xs text-dark-300 leading-relaxed">{node.body}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Kicker */}
      <div className="mt-12 max-w-3xl mx-auto text-center space-y-3">
        <p className="text-lg text-dark-100 leading-relaxed">
          All six. Every turn. That's the kind of fidelity you can actually feel — fifty turns in, a hundred, three chapters deep.
        </p>
        <p className="text-sm text-dark-300">
          And because each subsystem focuses only on what <em>this</em> scene needs, the prose stays sharp turn after turn.
        </p>
      </div>
    </section>
  );
}

export function LandingPage() {
  return (
    <div className="min-h-screen bg-dark-900 text-dark-100 relative overflow-x-hidden">
      {/* Background image, faded */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
        style={{ backgroundImage: "url('/textborne_concept_realistic_leatherbook.jpeg')" }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 bg-gradient-to-b from-black/60 via-dark-900/90 to-dark-900"
        aria-hidden="true"
      />

      <div className="relative z-10">
        {/* Top bar */}
        <header className="flex items-center justify-between px-6 py-4 md:px-10 md:py-6">
          <div className="flex items-center gap-2">
            <img src="/vite.svg" alt="" className="h-6 w-6" />
            <span className="text-xl font-bold text-primary-400">Textborne</span>
          </div>
          <nav className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-dark-200 hover:text-dark-50 px-3 py-2">
              Sign in
            </Link>
            <Link to="/register" className="btn-primary text-sm">
              Get started
            </Link>
          </nav>
        </header>

        {/* Hero */}
        <section className="px-6 md:px-10 pt-12 pb-16 md:pt-20 md:pb-24 max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-2 space-y-6">
              <div className="inline-block text-xs uppercase tracking-[0.2em] text-primary-400/80 border border-primary-500/30 rounded-full px-3 py-1">
                Reality Simulation Engine
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-dark-50 leading-tight">
                A text adventure that remembers <span className="text-primary-400">everything</span>.
                <br />
                <span className="text-dark-100">And uses all of it.</span>
              </h1>
              <p className="text-lg text-dark-200 leading-relaxed">
                An AI narrative engine tuned for fidelity. Describe a premise, play as anyone, and watch the story remember every choice — and rise to meet it, turn after turn.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Link to="/register" className="btn-primary px-6 py-3 text-base text-center">
                  Get started
                </Link>
                <Link to="/login" className="btn-secondary px-6 py-3 text-base text-center">
                  I have an account
                </Link>
              </div>
              <p className="text-sm text-dark-300 pt-1">
                Credits required for generation. Purchases start at $5.
              </p>
            </div>

            <div className="lg:col-span-3 space-y-3">
              <DemoExchange />
              <p className="text-sm text-dark-200 text-center lg:text-left">
                Most apps give the model a chat log. We give it a living world.
              </p>
            </div>
          </div>
        </section>

        {/* Architecture showcase */}
        <ArchitectureSection />

        {/* How it works */}
        <section className="px-6 md:px-10 py-16 max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-dark-50 mb-3">
              Designed for the story you want to live in.
            </h2>
            <p className="text-dark-300 max-w-2xl mx-auto">
              Every story you start in Textborne is yours. Private by default. Immersive by design. Shaped turn by turn.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <FeatureCard
              icon="✍️"
              title="Describe a premise"
              body="Pick from 9 hand-crafted templates or write your own. A few sentences is enough — the engine scaffolds characters, places, and conflicts automatically."
            />
            <FeatureCard
              icon="🎭"
              title="Play as anyone"
              body="You're not stuck as 'the player.' Inhabit a Victorian detective, a starship early-waker, a small-town newcomer. Every NPC has their own goals and will act on them."
            />
            <FeatureCard
              icon="🧠"
              title="Everything carries forward"
              body="Characters remember what you said three chapters ago. Locations evolve. Threads you planted pay off later. The story is one continuous canvas — not a conversation that resets."
            />
            <FeatureCard
              icon="⚡"
              title="Tuned for fidelity, not token burn"
              body="Most AI roleplay crams irrelevant history into every call and hopes the model notices. We give the model exactly what the current scene needs — so the prose sharpens where it matters."
            />
          </div>
        </section>

        {/* Final CTA */}
        <section className="px-6 md:px-10 py-16 md:py-24 max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-dark-50 mb-4">
            Ready to start a story?
          </h2>
          <p className="text-dark-200 text-lg mb-8 max-w-xl mx-auto">
            Sign up takes ten seconds. Build a world or pick a premise, and the engine takes it from there.
          </p>
          <Link to="/register" className="btn-primary px-8 py-3 text-base inline-block">
            Create your account →
          </Link>
          <p className="text-xs text-dark-400 mt-4">
            Already playing? <Link to="/login" className="text-primary-400 hover:text-primary-300 underline">Sign in</Link>.
          </p>
        </section>

        <footer className="px-6 md:px-10 py-8 border-t border-dark-600 text-center text-xs text-dark-400">
          Textborne — crafted for readers, writers, and people who still like books.
        </footer>
      </div>
    </div>
  );
}
